import {
  AUTH_API_BASE,
  FARE_ENGINE_API_BASE,
  OFFER_API_BASE,
  ROUTES_ADMIN_API_BASE,
} from '@/config/apiConfig';

/**
 * Recognized backend microservices supported for intent-driven pre-warming.
 */
export type BackendServiceKey =
  | 'fareEngine'
  | 'admin'
  | 'offer'
  | 'auth';

/**
 * Configuration options for dispatching a pre-warming probe.
 */
export interface PrewarmOptions {
  /** Relative health check endpoint path (default: '/health') */
  path?: string;
  /** Timeout in milliseconds before aborting the probe (default: 10000ms) */
  timeoutMs?: number;
  /** In-memory cooldown in milliseconds before allowing another probe to the same target (default: 5 minutes matching ACA 300s scale-to-zero) */
  cooldownMs?: number;
  /** Custom HTTP headers to include with the probe */
  headers?: Record<string, string>;
  /** Optional custom debounce key (defaults to service key or target URL) */
  debounceKey?: string;
}

/**
 * Interface contract for backend pre-warming operations.
 */
export interface IPrewarmService {
  prewarm(service: BackendServiceKey, options?: PrewarmOptions): void;
  prewarmUrl(baseUrl: string | undefined | null, options?: PrewarmOptions): void;
  clearCooldowns(): void;
  isInFlight(key: string): boolean;
}

const DEFAULT_TIMEOUT_MS = 10000;
const DEFAULT_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes matching ACA scale-to-zero envelope
const DEFAULT_PROBE_PATH = '/health';
const MAX_REGISTRY_SIZE = 50;
const STORAGE_PREFIX = 'duri_prewarm_';

const DEFAULT_SERVICE_URLS: Record<BackendServiceKey, string | undefined> = {
  fareEngine: FARE_ENGINE_API_BASE,
  admin: ROUTES_ADMIN_API_BASE,
  offer: OFFER_API_BASE,
  auth: AUTH_API_BASE,
};

/**
 * Enterprise pre-warming service for scale-to-zero container architectures (ACA).
 * Provides parameter-driven, deduplicated, non-blocking health probing with telemetry headers
 * and session-persistent cooldown tracking across hard page reloads.
 */
export class PrewarmService implements IPrewarmService {
  private readonly cooldownRegistry = new Map<string, number>();
  private readonly inFlightRegistry = new Set<string>();

  /**
   * Resolves a known service key to its active base URL.
   */
  public resolveBaseUrl(service: BackendServiceKey): string | undefined {
    return DEFAULT_SERVICE_URLS[service];
  }

  /**
   * Normalizes base URL and path to construct a clean probe endpoint.
   */
  public buildProbeUrl(baseUrl: string, path: string = DEFAULT_PROBE_PATH): string {
    return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }

  /**
   * Returns whether a prewarm probe is currently in-flight for a key.
   */
  public isInFlight(key: string): boolean {
    return this.inFlightRegistry.has(key);
  }

  /**
   * Reads the last probe timestamp from in-memory registry or sessionStorage fallback.
   */
  private getLastPingTime(key: string): number {
    const memTime = this.cooldownRegistry.get(key) ?? 0;
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return memTime;
    }
    try {
      const stored = window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
      const storedTime = stored ? Number(stored) : 0;
      return Math.max(memTime, Number.isNaN(storedTime) ? 0 : storedTime);
    } catch {
      return memTime;
    }
  }

  /**
   * Persists the last probe timestamp in-memory and in sessionStorage.
   */
  private setLastPingTime(key: string, timestamp: number): void {
    if (this.cooldownRegistry.size >= MAX_REGISTRY_SIZE) {
      this.cooldownRegistry.clear();
    }
    this.cooldownRegistry.set(key, timestamp);

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, timestamp.toString());
      } catch {
        // Quota exceeded or private mode, memory map is already updated
      }
    }
  }

  /**
   * Dispatches a smart, non-blocking pre-warm probe with in-flight deduplication
   * and per-target cooldown matching Azure Container Apps scale-to-zero timing.
   */
  public prewarmUrl(
    baseUrl: string | undefined | null,
    options?: PrewarmOptions
  ): void {
    if (!baseUrl) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;

    const debounceKey = options?.debounceKey ?? baseUrl;
    const cooldownMs = options?.cooldownMs ?? DEFAULT_COOLDOWN_MS;
    const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const path = options?.path ?? DEFAULT_PROBE_PATH;

    // Deduplication: never spawn concurrent duplicate probes for the same target
    if (this.inFlightRegistry.has(debounceKey)) {
      return;
    }

    // Cooldown check (in-memory + sessionStorage): prevent redundant calls if container is already warm
    const now = Date.now();
    const lastPing = this.getLastPingTime(debounceKey);
    if (now - lastPing < cooldownMs) {
      return;
    }

    this.setLastPingTime(debounceKey, now);
    this.inFlightRegistry.add(debounceKey);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      const probeUrl = this.buildProbeUrl(baseUrl, path);

      fetch(probeUrl, {
        method: 'GET',
        headers: {
          'X-Prewarm': '1',
          Accept: 'application/json, text/plain, */*',
          ...options?.headers,
        },
        signal: controller.signal,
        keepalive: true,
      })
        .catch(() => {
          // Non-blocking: background warmup swallows network / abort exceptions
        })
        .finally(() => {
          clearTimeout(timeoutId);
          this.inFlightRegistry.delete(debounceKey);
        });
    } catch {
      this.inFlightRegistry.delete(debounceKey);
    }
  }

  /**
   * Pre-warms a recognized backend microservice by key.
   */
  public prewarm(service: BackendServiceKey, options?: PrewarmOptions): void {
    const baseUrl = this.resolveBaseUrl(service);
    this.prewarmUrl(baseUrl, {
      ...options,
      debounceKey: options?.debounceKey ?? service,
    });
  }

  /**
   * Resets cooldown timestamps (useful in testing and session reset).
   */
  public clearCooldowns(): void {
    this.cooldownRegistry.clear();
    this.inFlightRegistry.clear();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        const storage = window.sessionStorage;
        Object.keys(storage)
          .filter((k) => k.startsWith(STORAGE_PREFIX))
          .forEach((k) => storage.removeItem(k));
      } catch {
        // Ignore storage exceptions
      }
    }
  }
}

/** Global singleton instance */
export const prewarmService = new PrewarmService();

/**
 * Functional API: Pre-warms a backend microservice by key.
 */
export function prewarmBackend(
  service: BackendServiceKey,
  options?: PrewarmOptions
): void {
  prewarmService.prewarm(service, options);
}

/**
 * Functional API: Pre-warms any arbitrary backend base URL.
 */
export function prewarmBackendUrl(
  baseUrl: string | undefined | null,
  options?: PrewarmOptions
): void {
  prewarmService.prewarmUrl(baseUrl, options);
}

/**
 * Convenience alias for Fare Engine pre-warming.
 */
export function prewarmFareEngine(options?: PrewarmOptions): void {
  prewarmBackend('fareEngine', options);
}

/**
 * Convenience alias for Admin API pre-warming.
 */
export function prewarmAdminApi(options?: PrewarmOptions): void {
  prewarmBackend('admin', options);
}
